/**
 * UTM / affiliate attribution capture.
 *
 * On first landing (or whenever new attribution params appear in the URL) we
 * persist them to sessionStorage so they survive client-side navigation.
 * Booking-engine handoffs and lead submissions read from getAttribution().
 */

const STORAGE_KEY = "sv_attribution";
const VID_KEY = "sv_vid";

const getVid = () => {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return "";
  }
};

const KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ref",
  "aff",
  "affiliate",
  "gclid",   // Google Ads click id
  "fbclid",  // Facebook click id
];

const readFromUrl = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const found = {};
  KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) found[k] = v.slice(0, 120);
  });
  return found;
};

const load = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const save = (obj) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // sessionStorage unavailable — silent fail
  }
};

/**
 * Capture on page load. Merges URL params over any existing session data
 * so a new campaign click updates attribution mid-session.
 */
export const captureAttribution = () => {
  getVid();
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length === 0) return load();
  const merged = { ...load(), ...fromUrl, landing_page: window.location.pathname, captured_at: new Date().toISOString() };
  save(merged);
  return merged;
};

export const getAttribution = () => {
  const vid = getVid();
  const data = load();
  return vid ? { ...data, vid } : data;
};

/** Return attribution keys as a URLSearchParams-appendable object. */
export const attributionQuery = () => {
  const a = load();
  const out = {};
  Object.keys(a).forEach((k) => {
    if (KEYS.includes(k)) out[k] = a[k];
  });
  return out;
};
